/**
 * Properties of the material currently loaded. These are measured facts about
 * the media, not tunable settings — changing one without regenerating the
 * media will desynchronise the player.
 *
 * Historically these were compile-time constants for the single built-in
 * mosaic. The player now supports several recordings, so they are *live
 * bindings* set once by `applyRecording()` before the UI mounts. ES module
 * imports observe reassignment, so importers keep working unchanged — but the
 * active recording must never change under a running engine. Switching
 * recordings reloads the page (see ui/app.ts), which is what guarantees that.
 *
 * The initial values are the built-in mosaic's, so anything that runs without
 * an explicit `applyRecording()` — `npm run verify` in particular — exercises
 * the exact numbers the original player shipped with.
 */

export interface VideoTier {
  url: string
  width: number
  height: number
  /** Codec string for `VideoDecoder.isConfigSupported`. */
  codec: string
  /**
   * Whether this tier is only safe with a hardware decoder. Software-decoding
   * 3840x1920 at 60 fps drops frames on most machines, and `isConfigSupported`
   * reports only whether a codec is *supported*, never whether it is fast
   * enough — so the hardware preference has to be stated explicitly.
   */
  requireHardware: boolean
}

export interface AudioBank {
  /** Container extension, informational — the URLs already carry it. */
  format: string
  /** One URL per stem, ordered to match `trackNames`. */
  urls: string[]
}

/** A recording resolved to playable URLs. Built by engine/recordings.ts. */
export interface Recording {
  id: string
  title: string
  sampleRate: number
  loopSamples: number
  mediaSeconds: number
  videoFps: number
  cols: number
  rows: number
  trackNames: string[]
  tempoPresets: number[]
  audio: Record<number, AudioBank>
  /** Preference-ordered tiers; `half` may be empty. */
  videoFull: VideoTier[]
  videoHalf: VideoTier[]
  posterUrl: string | null
}

/**
 * Kept as an alias so the engine's signatures still name the concept. With
 * uploaded recordings the presets are data, not a closed union.
 */
export type TempoPercent = number

/** Every stem is mono 48 kHz. The AudioContext is pinned to this rate. */
export const SAMPLE_RATE = 48_000

/**
 * The built-in mosaic's musical loop: exactly 1,148,160 samples = 23.920 s.
 * An exact whole number of samples is what makes the loop seamless.
 */
export let LOOP_SAMPLES = 1_148_160

/** Musical loop length in seconds (loopSamples / sampleRate). */
export let LOOP_SECONDS = LOOP_SAMPLES / SAMPLE_RATE

/**
 * Full media length. The built-in video runs 24.05 s against a 23.92 s musical
 * loop — the extra 0.13 s is tail that must never be shown. Musical time and
 * video time are the same clock; the tail is simply never reached. Do not
 * rescale one clock onto the other.
 */
export let VIDEO_SECONDS = 24.05

export let VIDEO_FPS = 60

/**
 * The loop rarely lands on a frame boundary (23.92 s x 60 fps = 1435.2). The
 * frame scheduler absorbs this as a bounded rounding error of at most one
 * frame interval; it never accumulates.
 */
export let LAST_LOOP_FRAME = Math.floor(LOOP_SECONDS * VIDEO_FPS)

export let COLS = 4
export let ROWS = 2
export let TRACK_COUNT = COLS * ROWS

export let TRACK_NAMES: readonly string[] = [
  'Clave',
  'Cata',
  'Erikundi',
  'Quinto',
  'Conga',
  'Tumba',
  'Coro 1',
  'Coro 2',
]

/**
 * Tempo percents with an audio bank. For the built-in mosaic every preset
 * divides 1,148,160 evenly, so the loop stays sample-locked at each one;
 * `scripts/verify-sync.mjs` asserts this. Uploaded recordings usually ship
 * only their original tempo.
 */
export let TEMPO_PRESETS: readonly number[] = [25, 50, 75, 100]

/**
 * Ramp applied when a track is muted or unmuted. Long enough to avoid a click,
 * short enough to feel instant.
 */
export const MUTE_RAMP_SECONDS = 0.006

let active: Recording | null = null

/** The recording the engine is playing. Throws before `applyRecording`. */
export function activeRecording(): Recording {
  if (!active) throw new Error('applyRecording() must run before the engine starts.')
  return active
}

/**
 * Install a recording as the source of every timing fact above. Must be called
 * exactly once, before the UI mounts — never while an engine is live.
 */
export function applyRecording(rec: Recording): void {
  active = rec
  LOOP_SAMPLES = rec.loopSamples
  LOOP_SECONDS = rec.loopSamples / rec.sampleRate
  VIDEO_SECONDS = rec.mediaSeconds
  VIDEO_FPS = rec.videoFps
  LAST_LOOP_FRAME = Math.floor(LOOP_SECONDS * VIDEO_FPS)
  COLS = rec.cols
  ROWS = rec.rows
  TRACK_COUNT = rec.cols * rec.rows
  TRACK_NAMES = rec.trackNames
  TEMPO_PRESETS = rec.tempoPresets
}
